<template>
  <div class="glm">

    <!-- ── Cabeçalho ──────────────────────────────────────── -->
    <div class="glm__top">
      <div>
        <h2 class="glm__title">Convidados</h2>
        <p class="glm__subtitle">
          <template v-if="groups.length === 0">Nenhum grupo cadastrado ainda</template>
          <template v-else>
            {{ totalConfirmed }} confirmad{{ totalConfirmed === 1 ? 'o' : 'os' }} de {{ totalInvites }} convidado{{ totalInvites === 1 ? '' : 's' }}
          </template>
        </p>
      </div>
      <button class="glm__add-btn" @click="openAddModal">
        <i class="fas fa-plus"></i> Novo grupo
      </button>
    </div>

    <!-- ── Filtros ─────────────────────────────────────────── -->
    <div class="glm__filters">
      <button
        v-for="f in filters"
        :key="f.value"
        class="glm__filter-btn"
        :class="{ 'glm__filter-btn--active': activeFilter === f.value }"
        @click="activeFilter = f.value"
      >
        {{ f.label }}
        <span class="glm__filter-count" v-if="countByFilter(f.value) > 0">
          {{ countByFilter(f.value) }}
        </span>
      </button>
    </div>

    <!-- ── Lista ───────────────────────────────────────────── -->
    <div v-if="loading" class="glm__loading">
      <i class="fas fa-spinner fa-spin me-2"></i> Carregando grupos...
    </div>

    <div class="glm__list" v-else-if="filteredGroups.length > 0">
      <div
        v-for="group in filteredGroups"
        :key="group.id"
        class="glm__item"
        :class="`glm__item--${group.status}`"
      >
        <!-- Nome + código + badge -->
        <div class="glm__item-head">
          <div class="glm__item-info">
            <span class="glm__item-name">{{ group.name }}</span>
            <span class="glm__item-code" title="Código do convite">{{ group.code }}</span>
          </div>
          <span class="glm__badge" :class="`glm__badge--${group.status}`">
            {{ statusLabel(group.status) }}
          </span>
        </div>

        <!-- Detalhe -->
        <div class="glm__item-detail" v-if="group.status === 'confirmed'">
          <i class="fas fa-circle-check me-1"></i>
          <strong>{{ group.confirmed_count }}</strong> de <strong>{{ group.invite_count }}</strong> pessoas confirmaram presença
          <span v-if="group.message" class="glm__item-quote">&nbsp;· "{{ group.message }}"</span>
        </div>
        <div class="glm__item-detail" v-else>
          <i class="fas fa-users me-1"></i>
          {{ group.invite_count }} {{ group.invite_count === 1 ? 'pessoa' : 'pessoas' }} neste grupo
        </div>

        <!-- Ações -->
        <div class="glm__item-actions">
          <button class="glm__btn-edit" @click="openEditModal(group)">
            <i class="fas fa-pen"></i> Editar
          </button>
          <button
            v-if="group.status !== 'confirmed'"
            class="glm__btn-wa"
            @click="copyMessage(group)"
          >
            <i class="fab fa-whatsapp"></i>
            {{ group.status === 'viewed' ? 'Reenviar' : 'Compartilhar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Estado vazio do filtro -->
    <div v-else-if="!loading && groups.length > 0" class="glm__empty-filter">
      <i class="fas fa-filter"></i>
      Nenhum grupo nesta categoria
    </div>

    <!-- Estado vazio total -->
    <div v-else-if="!loading" class="glm__empty">
      <div class="glm__empty-icon">👥</div>
      <p class="glm__empty-title">Ainda sem convidados</p>
      <p class="glm__empty-sub">
        Clique em <strong>Novo grupo</strong> para começar sua lista.
        Depois é só copiar a mensagem e mandar no WhatsApp!
      </p>
    </div>

    <!-- ── BottomModal: Adicionar grupo ───────────────────── -->
    <BottomModal :visible="showAddModal" @close="closeAddModal">
      <div class="glm__modal">
        <h3 class="glm__modal-title">Novo grupo de convidados</h3>

        <div class="glm__field">
          <label class="glm__label">Nome <span class="glm__required">*</span></label>
          <input
            v-model="form.name"
            type="text"
            class="glm__input"
            placeholder="Ex: Família da Maria, Amigos do trabalho…"
            maxlength="80"
          />
        </div>

        <div class="glm__field">
          <label class="glm__label">Quantas pessoas neste grupo? <span class="glm__required">*</span></label>
          <div class="glm__counter">
            <button class="glm__counter-btn" @click="form.invite_count = Math.max(1, form.invite_count - 1)">−</button>
            <span class="glm__counter-val">{{ form.invite_count }}</span>
            <button class="glm__counter-btn" @click="form.invite_count = Math.min(50, form.invite_count + 1)">+</button>
          </div>
          <span class="glm__hint">Máximo de pessoas que podem confirmar presença</span>
        </div>

        <div v-if="formError" class="glm__error">{{ formError }}</div>

        <div class="glm__modal-actions">
          <button class="glm__btn glm__btn--ghost" @click="closeAddModal">Cancelar</button>
          <button class="glm__btn glm__btn--primary" @click="addGroup" :disabled="addingGroup">
            <i class="fas fa-spinner fa-spin me-1" v-if="addingGroup"></i>
            {{ addingGroup ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </BottomModal>

    <!-- ── BottomModal: Editar grupo ──────────────────────── -->
    <BottomModal :visible="showEditModal" @close="closeEditModal">
      <div class="glm__modal">
        <h3 class="glm__modal-title">Editar grupo</h3>

        <div class="glm__field">
          <label class="glm__label">Nome</label>
          <input
            v-model="editForm.name"
            type="text"
            class="glm__input"
            maxlength="80"
          />
        </div>

        <div class="glm__field">
          <label class="glm__label">Quantas pessoas neste grupo?</label>
          <div class="glm__counter">
            <button class="glm__counter-btn" @click="editForm.invite_count = Math.max(1, editForm.invite_count - 1)">−</button>
            <span class="glm__counter-val">{{ editForm.invite_count }}</span>
            <button class="glm__counter-btn" @click="editForm.invite_count = Math.min(50, editForm.invite_count + 1)">+</button>
          </div>
        </div>

        <div v-if="editingGroup && editingGroup.status === 'confirmed'" class="glm__field">
          <label class="glm__label">Quantas confirmaram presença?</label>
          <div class="glm__counter">
            <button class="glm__counter-btn" @click="editForm.confirmed_count = Math.max(0, editForm.confirmed_count - 1)">−</button>
            <span class="glm__counter-val">{{ editForm.confirmed_count }}</span>
            <button class="glm__counter-btn" @click="editForm.confirmed_count = Math.min(editForm.invite_count, editForm.confirmed_count + 1)">+</button>
          </div>
          <p class="glm__field-hint">Máximo: {{ editForm.invite_count }} pessoas do grupo</p>
        </div>

        <div v-if="editError" class="glm__error">{{ editError }}</div>

        <div class="glm__modal-actions">
          <button class="glm__btn glm__btn--ghost" @click="closeEditModal">Cancelar</button>
          <button class="glm__btn glm__btn--primary" @click="saveEdit" :disabled="savingEdit">
            <i class="fas fa-spinner fa-spin me-1" v-if="savingEdit"></i>
            {{ savingEdit ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </BottomModal>

    <!-- ── BottomModal: Compartilhar convite ───────────────── -->
    <BottomModal :visible="showShareModal" @close="showShareModal = false">
      <div class="glm__modal">
        <h3 class="glm__modal-title">Convite para {{ shareGroup && shareGroup.name }}</h3>
        <p class="glm__modal-desc">
          Copie o texto abaixo e envie no WhatsApp para que
          <strong>{{ shareGroup && shareGroup.name }}</strong> possa confirmar presença.
        </p>
        <div class="glm__field">
          <textarea
            v-model="editableMessage"
            class="glm__textarea"
            rows="7"
          ></textarea>
        </div>
        <div class="glm__modal-actions">
          <button class="glm__btn glm__btn--ghost" @click="showShareModal = false">Fechar</button>
          <button class="glm__btn glm__btn--wa" @click="copyAndSend">
            <i class="fab fa-whatsapp"></i> Copiar mensagem
          </button>
        </div>
      </div>
    </BottomModal>

  </div>
</template>

<script>
import BottomModal from '@/components/BottomModal.vue';
import { toast } from 'vue3-toastify';
import api from '@/services/api';

export default {
  name: 'GuestListManager',
  components: { BottomModal },

  props: {
    eventId:       { type: String, required: true },
    eventShareUrl: { type: String, default: '' },
    eventName:     { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      activeFilter: 'all',
      filters: [
        { value: 'all',       label: 'Todos' },
        { value: 'created',   label: 'A enviar' },
        { value: 'viewed',    label: 'Vistos' },
        { value: 'confirmed', label: 'Confirmados' },
      ],

      groups: [],

      showAddModal: false,
      form: { name: '', invite_count: 1 },
      formError: '',
      addingGroup: false,

      showEditModal: false,
      editingGroup: null,
      editForm: { name: '', invite_count: 1, confirmed_count: 0 },
      editError: '',
      savingEdit: false,

      showShareModal: false,
      shareGroup: null,
      editableMessage: '',
    };
  },

  computed: {
    filteredGroups() {
      if (this.activeFilter === 'all') return this.groups;
      return this.groups.filter(g => g.status === this.activeFilter);
    },
    totalInvites() {
      return this.groups.reduce((s, g) => s + (g.invite_count || 0), 0);
    },
    totalConfirmed() {
      return this.groups
        .filter(g => g.status === 'confirmed')
        .reduce((s, g) => s + (g.confirmed_count || 0), 0);
    },
  },

  mounted() {
    this.fetchGroups();
  },

  methods: {
    async fetchGroups() {
      this.loading = true;
      try {
        const { data } = await api.get(`/events/${this.eventId}/guest-groups`);
        this.groups = data.data || [];
      } catch (err) {
        toast.error('Erro ao carregar grupos de convidados.');
      } finally {
        this.loading = false;
      }
    },

    countByFilter(value) {
      if (value === 'all') return this.groups.length;
      return this.groups.filter(g => g.status === value).length;
    },

    statusLabel(status) {
      return { created: 'A enviar', viewed: 'Visto', confirmed: 'Confirmado' }[status] || status;
    },

    openAddModal() {
      this.form = { name: '', invite_count: 1 };
      this.formError = '';
      this.showAddModal = true;
    },
    closeAddModal() { this.showAddModal = false; },

    async addGroup() {
      this.formError = '';
      if (!this.form.name.trim()) { this.formError = 'Informe o nome do grupo.'; return; }
      this.addingGroup = true;
      try {
        const { data } = await api.post(`/events/${this.eventId}/guest-groups`, {
          name: this.form.name.trim(),
          invite_count: this.form.invite_count,
        });
        this.groups.unshift(data);
        this.showAddModal = false;
        toast.success('Grupo criado com sucesso!');
      } catch (err) {
        this.formError = err.response?.data?.message || 'Erro ao criar grupo.';
      } finally {
        this.addingGroup = false;
      }
    },

    openEditModal(group) {
      this.editingGroup = { ...group };
      this.editForm = { name: group.name, invite_count: group.invite_count, confirmed_count: group.confirmed_count || 0 };
      this.editError = '';
      this.showEditModal = true;
    },
    closeEditModal() { this.showEditModal = false; this.editingGroup = null; },

    async saveEdit() {
      this.editError = '';
      if (!this.editForm.name.trim()) { this.editError = 'Informe o nome do grupo.'; return; }
      this.savingEdit = true;
      try {
        const payload = {
          name: this.editForm.name.trim(),
          invite_count: this.editForm.invite_count,
        };
        const { data } = await api.patch(`/events/${this.eventId}/guest-groups/${this.editingGroup.id}`, payload);
        const idx = this.groups.findIndex(g => g.id === this.editingGroup.id);
        if (idx !== -1) this.groups.splice(idx, 1, data);
        this.showEditModal = false;
        this.editingGroup = null;
        toast.success('Grupo atualizado!');
      } catch (err) {
        this.editError = err.response?.data?.message || 'Erro ao salvar grupo.';
      } finally {
        this.savingEdit = false;
      }
    },

    buildMessage(group) {
      const base = this.eventShareUrl || window.location.origin + '/event/' + this.eventId;
      const url = `${base}?view=convite&code=${group.code}`;
      return `Olá, ${group.name}! 🎉\nVocê está convidado para ${this.eventName || 'o nosso evento'}!\n\nConfirme sua presença pelo link abaixo:\n${url}`;
    },

    copyMessage(group) {
      this.shareGroup = group;
      this.editableMessage = this.buildMessage(group);
      this.showShareModal = true;
    },

    copyAndSend() {
      const text = this.editableMessage;
      const name = this.shareGroup.name;
      const done = () => {
        toast.success(`Copiado! Agora cole no WhatsApp de ${name}.`);
        this.showShareModal = false;
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(done).catch(() => { this._copyFallback(text); done(); });
      } else {
        this._copyFallback(text);
        done();
      }
    },

    _copyFallback(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
    },
  },
};
</script>

<style scoped>
.glm {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 700px;
  margin: 0 auto;
}

/* ── Topo ─────────────────────────────────────────────── */
.glm__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.glm__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.15rem;
}
.glm__subtitle {
  font-size: 0.82rem;
  color: #6c757d;
  margin: 0;
}
.glm__add-btn {
  background: #ff50a3;
  color: #fff;
  border: none;
  border-radius: 9px;
  padding: 0.55rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  transition: background 0.15s;
}
.glm__add-btn:hover { background: #e8408f; }

/* ── Filtros ──────────────────────────────────────────── */
.glm__filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.glm__filter-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 99px;
  padding: 0.35rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}
.glm__filter-btn:hover { border-color: #ff50a3; color: #ff50a3; }
.glm__filter-btn--active {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
  font-weight: 600;
}
.glm__filter-count {
  background: rgba(255,255,255,0.3);
  border-radius: 99px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.05rem 0.45rem;
  min-width: 1.2rem;
  text-align: center;
}
.glm__filter-btn:not(.glm__filter-btn--active) .glm__filter-count {
  background: #f1f3f5;
  color: #495057;
}

/* ── Lista ────────────────────────────────────────────── */
.glm__list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

/* ── Item ─────────────────────────────────────────────── */
.glm__item {
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.glm__item--confirmed {
  border-color: #bbf7d0;
  background: #f6fef9;
}

.glm__item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.glm__item-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.glm__item-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.glm__item-code {
  font-family: 'Courier New', monospace;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #fff;
  background: #adb5bd;
  border-radius: 5px;
  padding: 0.1rem 0.45rem;
  flex-shrink: 0;
}
.glm__item--confirmed .glm__item-code { background: #15803d; }
.glm__item--viewed .glm__item-code { background: #3b82f6; }

.glm__item-detail {
  font-size: 0.8rem;
  color: #6c757d;
}
.glm__item--confirmed .glm__item-detail { color: #15803d; }
.glm__item-quote {
  font-style: italic;
  color: #6c757d;
  font-weight: 400;
}

/* ── Badge ────────────────────────────────────────────── */
.glm__badge {
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 99px;
  padding: 0.2rem 0.65rem;
  white-space: nowrap;
  flex-shrink: 0;
}
.glm__badge--created   { background: #f1f3f5; color: #6c757d; }
.glm__badge--viewed    { background: #dbeafe; color: #1d4ed8; }
.glm__badge--confirmed { background: #dcfce7; color: #15803d; }

/* ── Ações ────────────────────────────────────────────── */
.glm__item-actions {
  display: flex;
  gap: 0.4rem;
  margin-top: 0.15rem;
}
.glm__btn-edit {
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #adb5bd;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.15s;
  flex-shrink: 0;
}
.glm__btn-edit:hover { border-color: #ff50a3; color: #ff50a3; }

.glm__btn-wa {
  flex: 1;
  border: none;
  background: #25d366;
  color: #fff;
  border-radius: 6px;
  padding: 0.28rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  transition: background 0.15s;
}
.glm__btn-wa:hover { background: #1ebe5a; }

/* ── Vazio filtro ─────────────────────────────────────── */
.glm__empty-filter {
  text-align: center;
  padding: 2rem;
  font-size: 0.85rem;
  color: #adb5bd;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.glm__empty-filter i { font-size: 1.5rem; }

/* ── Vazio total ──────────────────────────────────────── */
.glm__loading {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.glm__empty {
  text-align: center;
  padding: 2.5rem 1.5rem;
  border: 2px dashed #e9ecef;
  border-radius: 12px;
}
.glm__empty-icon { font-size: 2.25rem; display: block; margin-bottom: 0.75rem; }
.glm__empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: #495057;
  margin: 0 0 0.4rem;
}
.glm__empty-sub {
  font-size: 0.83rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.6;
}

/* ── Modal ────────────────────────────────────────────── */
.glm__modal { padding: 1.5rem 1.5rem 2rem; }
.glm__modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 1.25rem;
}
.glm__textarea {
  width: 100%;
  background: #f8f9fa;
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
  color: #1a1a2e;
  line-height: 1.7;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  box-sizing: border-box;
  font-family: inherit;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.04);
}
.glm__textarea:focus {
  border-color: #ff50a3;
  background: #fff;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.04), 0 0 0 3px rgba(255,80,163,0.08);
}

.glm__btn--wa {
  flex: 1;
  border: none;
  background: #25d366;
  color: #fff;
  border-radius: 9px;
  padding: 0.65rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: background 0.15s;
}
.glm__btn--wa:hover { background: #1ebe5a; }

/* Toast */
.glm__toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a2e;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.7rem 1.25rem;
  border-radius: 99px;
  white-space: nowrap;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
}
.glm-toast-enter-active, .glm-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.glm-toast-enter-from, .glm-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(8px); }

.glm__modal-icon { font-size: 2rem; margin-bottom: 0.5rem; }
.glm__modal-desc {
  font-size: 0.88rem;
  color: #495057;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}
.glm__field { margin-bottom: 1rem; }
.glm__label {
  display: block;
  font-size: 0.83rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.4rem;
}
.glm__required { color: #ff50a3; }
.glm__input {
  width: 100%;
  border: 1.5px solid #dee2e6;
  border-radius: 9px;
  padding: 0.65rem 0.9rem;
  font-size: 0.92rem;
  color: #1a1a2e;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.glm__input:focus { border-color: #ff50a3; }
.glm__hint { font-size: 0.73rem; color: #adb5bd; margin-top: 0.3rem; display: block; }
.glm__error { font-size: 0.8rem; color: #dc3545; margin-bottom: 0.75rem; }
.glm__field-hint { font-size: 0.78rem; color: #6c757d; margin: 0.3rem 0 0; }

.glm__counter {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.25rem;
}
.glm__counter-btn {
  width: 42px;
  height: 42px;
  border-radius: 9px;
  border: 1.5px solid #dee2e6;
  background: #f8f9fa;
  font-size: 1.3rem;
  font-weight: 700;
  color: #495057;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.glm__counter-btn:hover { border-color: #ff50a3; color: #ff50a3; background: #fff0f7; }
.glm__counter-val {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  min-width: 2rem;
  text-align: center;
}
.glm__modal-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
}
.glm__btn {
  flex: 1;
  padding: 0.65rem;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.glm__btn--primary { background: #ff50a3; color: #fff; }
.glm__btn--primary:hover:not(:disabled) { background: #e8408f; }
.glm__btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.glm__btn--ghost { background: #f8f9fa; color: #6c757d; border: 1px solid #dee2e6; }
.glm__btn--ghost:hover { background: #e9ecef; }

@media (max-width: 767px) {
  .glm { padding: 1rem; }
}
</style>
